import { Grid } from "@mui/material";
import loyaltyV2Icon from "assets/loyalty-icon-v2.png";
import PlusIcon from "assets/plus-icon.svg";
import classNames from "classnames";
import CloseIconButton from "components/AppModalDialogsV2/Dialogs/lib/CloseIconButton";
import Button from "components/Buttons/ThemeButton";
import useCalculateScale from "components/PopUpManager/hooks/useCalculateScale";
import { useGoogleAnalyticsContext, useWebStoreContext } from "contexts";
import {
	IShopPurchaseOffer,
	IShopPurchaseOfferSingleItem
} from "contexts/WebStoreProvider/types";
import { useAppDispatch, useAppSelector } from "hooks";
import useRewardsById from "hooks/useRewardsById";
import { WsDialogs } from "interfaces";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { featureTrackerAPI, offersApi } from "redux/apis";
import {
	onClosePopUp,
	onDIPtrigger,
	// selectPoisBountyV3State,
	selectPopUpManagerState
} from "redux/slices";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
// import { CardFinalMessage } from "styles/general_styles";
import { BonusFlag } from "./lib/Flag";
import {
	Container,
	ContentBackground,
	Plus,
	RewardImage,
	Text
} from "./styles";

interface SingleItemInfoProps {}

export const SingleItemInfo: React.FC<SingleItemInfoProps> = () => {
	const buyButtonRef = useRef(null);
	const dispatch = useAppDispatch();
	const scale = useCalculateScale(378, 380);
	const { openDialog } = useDialogModalRedux();
	const { variant } = useAppSelector(selectPopUpManagerState);
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const {
		authToken,
		currentOffer,
		handleRefresh,
		openLoginToContinueDialog,
		postUserMetadata,
		setActiveItemToBeClicked,
		setCurrentOffer,
		store,
		suppressOrderConfirmation,
		userTracker,
		wsStoreBuy
	} = useWebStoreContext();
	const { data } = offersApi.endpoints.getOffers.useQueryState({
		offerType: "singleItem",
		isAuthenticated: !!authToken?.value
	});
	const [isCurrentOffer, setIsCurrentOffer] = useState<boolean>(false);
	const [offerData, setOfferData] = useState<
		IShopPurchaseOfferSingleItem & IShopPurchaseOffer
	>();
	const displayRewards = useRewardsById(offerData?.rewardId as string);
	// Temporarily hidden per product request; restore this state with the JSX below
	// to re-enable the Poi's Bounty messaging.
	// const { canContribute: isPoisActive } = useAppSelector(
	// 	selectPoisBountyV3State
	// );

	const firstPurchaseText = useMemo(() => {
		return (
			offerData?.firstPurchaseRewardBonus &&
			store?.globalConfig
				?.find((c) => c.id === "first_purchase_badge_text")
				?.value?.replace(
					"{amount}",
					Number(offerData.firstPurchaseRewardBonus).toLocaleString("en-US")
				)
		);
	}, [offerData?.firstPurchaseRewardBonus, store?.globalConfig]);

	const resetCurrentOffer = () => {
		setIsCurrentOffer(false);
		setCurrentOffer(null);
	};

	const cbClose = () => {
		resetCurrentOffer();
		reportEvent("dialog_x_closed", { source: "xsolla_dialog" });
		handleRefresh();
		dispatch(onDIPtrigger({ triggerType: "cache-mode" }));
	};

	const onClose = () => {
		dispatch(
			featureTrackerAPI.util.invalidateTags([
				{
					type: "featureTrackerTag",
					id: "drip-offer"
				}
			])
		);
		dispatch(onClosePopUp());
	};

	const onClickBuy = async () => {
		if (offerData) {
			// setCurrentOffer used for button states
			setCurrentOffer(buyButtonRef.current);
			const {
				campaignGroup,
				id: offerId,
				loyaltyPointCrossout,
				onlyNonPayers,
				price,
				priority,
				purchaseLimit,
				rewardCrossout,
				rewardId,
				sectionName
			} = offerData;
			if (authToken?.value) {
				const invoice = await wsStoreBuy({ offerId });
				if (invoice) {
					const timestampMs = new Date().getTime();
					openDialog(WsDialogs.XSOLLA_PURCHASE, {
						xsollaToken: invoice.xsollaToken,
						xsollaHost: invoice.xsollaHost,
						invoice,
						source: "offer_card",
						triggerType: displayRewards?.[0]?.id,
						pricePoint: price,
						cbClose,
						suppressOrderConfirmation,
						postUserMetadata,
						showClose: true,
						onPurchaseStarted: (data) => {
							reportEvent("purchase_started", {
								tuid: userTracker?.id as string,
								offer_id: offerId,
								reward_id: rewardId,
								priority: priority,
								price_in_cents: Math.round(price * 100),
								campaign_group: campaignGroup,
								section_name: sectionName,
								only_non_payers: onlyNonPayers,
								loyalty_point_crossout:
									typeof loyaltyPointCrossout !== "undefined",
								items_crossout: typeof rewardCrossout !== "undefined",
								firstPurchaseText,
								first_purchase_bonus: firstPurchaseText,
								invoice_id: invoice?.id,
								paystation_id: data?.payStation
							});
							reportPlaygamiEvent("tp.web_ga.purchase_flow", {
								event_name: "purchase_started",
								event_type: "web_paystation_purchase",
								action: "purchase_started",
								section_name: sectionName,
								purchase_flow_id: data?.payStation,
								session_engaged: authToken?.value ? "1" : "0",
								loyalty_point_crossout:
									typeof loyaltyPointCrossout !== "undefined",
								price_in_cents: Math.round(invoice.price * 100),
								page_location: window.location.href,
								engagement_time_msec: (
									Number(data?.data?.milliseconds) - timestampMs
								).toString(),
								reward_id: invoice?.rewardId,
								items_crossout: typeof rewardCrossout !== "undefined",
								offer_id: invoice?.offerId,
								ignore_referrer:
									process.env.REACT_APP_WITH_DEBUG === "true"
										? "true"
										: "false",
								page_title: location.pathname.replace("/", ""),
								page_referrer: invoice?.invoiceMetadata?.referer,
								priority: priority,
								invoice_id: invoice?.id
							});
						},
						onPurchaseSuccess: (data) => {
							reportEvent("purchase_success", {
								invoice_id: invoice?.id
							});
							reportPlaygamiEvent("tp.web_ga.purchase_flow", {
								event_name: "purchase_success",
								event_type: "web_paystation_purchase",
								action: "purchase_success",
								section_name: sectionName,
								purchase_flow_id: data?.version,
								session_engaged: authToken?.value ? "1" : "0",
								loyalty_point_crossout:
									typeof loyaltyPointCrossout !== "undefined",
								price_in_cents: Math.round(invoice.price * 100),
								page_location: window.location.href,
								engagement_time_msec: (
									Number(data?.milliseconds) - timestampMs
								).toString(),
								reward_id: invoice?.rewardId,
								items_crossout: typeof rewardCrossout !== "undefined",
								offer_id: invoice?.offerId,
								ignore_referrer:
									process.env.REACT_APP_WITH_DEBUG === "true"
										? "true"
										: "false",
								page_title: location.pathname.replace("/", ""),
								page_referrer: invoice?.invoiceMetadata?.referer,
								priority: priority,
								invoice_id: invoice?.id,
								transaction_id: data?.purchase_invoice_id?.toString()
							});
							dispatch(onClosePopUp());
						}
					});
				} else {
					resetCurrentOffer();
				}

				//NOTE: bfloyd error cases already handled for undefined/null invoice
			} else {
				// setActiveItemToBeClicked used for re-opening after authentication
				setActiveItemToBeClicked({
					type: "store_offer",
					offerId,
					onlyNonPayers,
					purchaseLimit
				});
				openLoginToContinueDialog();
			}
		}
	};

	useEffect(() => {
		reportEvent("single_items", {
			source: "info_button_click"
		});
	}, []);

	useEffect(() => {
		if (offerData) {
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "info_button_click",
				event_type: "shop_visual_update",
				action: "info_button_click",
				source: "single_items",
				offer_id: offerData?.id
			});
		}
	}, [offerData?.id]);

	useEffect(() => {
		if (data) {
			const id = variant?.split("::").slice(-1)?.[0];
			const offer = data?.offers.find(
				(offer: IShopPurchaseOffer) => offer.id === id
			) as IShopPurchaseOfferSingleItem & IShopPurchaseOffer;
			setOfferData(offer);
		}
	}, [data]);

	if (!offerData) {
		return <Fragment />;
	}

	const {
		bannerText,
		//description,
		firstPurchaseRewardBonus,
		loyaltyPointReward,
		price,
		rewardImageUrl,
		firstPurchaseBonusImageUrl
	} = offerData;

	return (
		<Container scale={scale}>
			<CloseIconButton onClose={onClose} />
			{bannerText && <BonusFlag text={bannerText} />}
			<Grid container spacing={1} position="relative" mt={3} mb={2}>
				<Grid item xs={firstPurchaseRewardBonus ? 6 : 12}>
					<Text color="#333">You get:</Text>
					<ContentBackground>
						<RewardImage src={rewardImageUrl} />
						<Text color="#000">
							{`${displayRewards?.[0]?.description} ${
								Number(displayRewards?.[0]?.description?.replaceAll(",", "")) >
								1
									? displayRewards?.[0]?.pluralLabel
									: displayRewards?.[0]?.singularLabel
							}`}
						</Text>
					</ContentBackground>
				</Grid>
				{firstPurchaseRewardBonus && (
					<>
						<Plus src={PlusIcon} alt="Plus" />
						<Grid item xs={6}>
							<Text color="#333333">First purchase bonus:</Text>
							<ContentBackground>
								<RewardImage src={firstPurchaseBonusImageUrl} />
								<Text color="#000">
									{`${Number(firstPurchaseRewardBonus).toLocaleString(
										"en-US"
									)} ${
										Number(firstPurchaseRewardBonus) > 1
											? displayRewards?.[0]?.pluralLabel
											: displayRewards?.[0]?.singularLabel
									}`}
								</Text>
							</ContentBackground>
						</Grid>
					</>
				)}
			</Grid>
			<Text color="#333" align="center">
				Earn Loyalty Points with every purchase
			</Text>
			<Text color="#333" align="center">
				and redeem for rewards!
			</Text>
			<Grid item my={2}>
				<ContentBackground>
					<RewardImage src={loyaltyV2Icon} alt="Loyalty points icon" />
					<Text color="#000">
						{Number(loyaltyPointReward).toLocaleString("en-US")} Loyalty Points
					</Text>
				</ContentBackground>
			</Grid>
			<Button
				ref={buyButtonRef}
				variant="contained"
				color="success"
				size="large"
				onClick={onClickBuy}
				sx={{
					width: "100%",
					paddingTop: "0.5rem",
					paddingBottom: "0.5rem"
				}}
				className={classNames({
					isCurrentOffer,
					isCurrentOfferSet: !!currentOffer
				})}
			>
				{price} USD
			</Button>
			{/*
				{isPoisActive && (
					<CardFinalMessage color="#333" className="mt">
						Contribute to Poi’s Bounty
					</CardFinalMessage>
				)}
			*/}
		</Container>
	);
};
